import {
  hasQualityIssue,
  isEpisodeExcluded,
  type EpisodeListFilters,
  type EpisodeSummary,
} from "@/api";

/**
 * 에피소드 목록의 표시 상태. 순수 파생만 둔다.
 *
 * 목록 행 배지와 숨김 필터가 같은 정의를 쓰게 하는 leaf 모듈이다. 판정 축은
 * `src/api/episodeReview.ts` 가 소유한다. 이 모듈은 판정 축을 표시 상태로만 바꾼다.
 */

/**
 * 행에 표시할 수 있는 표시.
 *
 * 학습에 영향을 주는 사실과 사람이 내린 판정만 표시한다. **"검토했는가"는 표시하지 않는다.**
 * 리뷰는 필수 절차가 아니므로 미검토는 밀린 일이 아니다. 미검토를 세거나 색칠하면 필요 없는
 * 일을 재촉하게 된다. 결과 축에도 같은 결정을 적용한다. **기본 성공(`task_failed=false`)은 배지를
 * 만들지 않는다.** 기본 성공은 사람이 성공을 확인했다는 뜻이 아니기 때문이다. 실패만 표시한다.
 */
export type EpisodeQualityFlag = "failure" | "issue" | "excluded";

export const EPISODE_QUALITY_FLAG_LABEL: Record<EpisodeQualityFlag, string> = {
  failure: "실패",
  issue: "결함",
  excluded: "제외됨",
};

/**
 * 행에 붙일 표시 목록(제외 → 결과 → 결함). 알릴 것이 없으면 빈 배열(배지 없음)이다.
 *
 * 축이 서로 독립이므로 하나로 합치지 않는다. `제외 > 이슈 > 통과` 처럼 하나로 합치면 결함이
 * 없는 행이 성공으로 읽힌다. 또 실패인데 결함이 기록된 행을 표시할 수 없다.
 */
export function episodeQualityFlags(episode: EpisodeSummary): readonly EpisodeQualityFlag[] {
  const flags: EpisodeQualityFlag[] = [];
  if (isEpisodeExcluded(episode.status)) {
    flags.push("excluded");
  }
  if (episode.task_failed) {
    flags.push("failure");
  }
  if (hasQualityIssue(episode.quality_tags)) {
    flags.push("issue");
  }
  return flags;
}

/**
 * 화면에 그릴 행만 남긴다. `includeExcluded` 가 false 이면 학습에서 제외된 행을 숨기고 리뷰 대상만
 * 남긴다. 저장 직후의 로컬 override 는 호출자가 **먼저** 적용해야 한다. 순서가 반대이면 방금
 * "제외" 로 저장한 행이 목록에 남는다.
 */
export function filterVisibleEpisodes(
  episodes: readonly EpisodeSummary[],
  includeExcluded: boolean,
): readonly EpisodeSummary[] {
  return includeExcluded
    ? episodes
    : episodes.filter((episode) => !isEpisodeExcluded(episode.status));
}

/**
 * 눈 토글의 기본 숨김을 지금 적용해야 하는지 반환한다.
 *
 * `표시 상태` 축을 직접 고르면 클라이언트 숨김을 끈다. 서버가 이미 그 축으로 좁혔는데 다시
 * 거르면, `제외됨` 을 골랐을 때 결과가 모두 사라진다. 두 컨트롤이 같은 사실을 다루므로 규칙도
 * 한 곳에 둔다. 필터 레인을 쓰는 두 표면이 이 함수를 같이 쓴다.
 *
 * `무효 사유` 축도 숨김을 끈다. 서버는 제외 여부와 관계없이 사유로 거른다. 채널 누락 에피소드는
 * 업로드 때부터 제외 상태라서, 숨기면 사유 필터의 결과가 모두 사라진다.
 */
export function shouldHideExcluded(includeExcluded: boolean, filters: EpisodeListFilters): boolean {
  return !includeExcluded && !excludedShownByFilters(filters);
}

/**
 * 조건이 숨김을 끄고 있으면 true 다(`shouldHideExcluded`). 그동안 눈 토글은 눌러도 목록이 바뀌지
 * 않는다. 그래서 두 표면은 토글을 "보는 중" 으로 표시하고 끈다.
 */
export function excludedShownByFilters(filters: EpisodeListFilters): boolean {
  return filters.status !== undefined || filters.invalid_reason !== undefined;
}

/**
 * 채널 누락 에피소드를 여는 필터다. 목록 열·카드·상세 제목·업로드 완료 화면의 배지가 이 값으로
 * 에피소드 탭을 연다. 사유 축 하나만 건다. 그러면 `shouldHideExcluded` 가 숨김을 끄므로 건수와 행
 * 수가 같다. 매번 새 객체를 돌려준다. 받는 쪽은 객체가 바뀔 때 필터를 다시 적용한다.
 */
export function missingChannelEpisodeFilters(): EpisodeListFilters {
  return { invalid_reason: "missing_channel" };
}
